import React, {
  useState, useCallback, useEffect, useMemo, useContext,
} from 'react';
import { useSelector } from 'react-redux';
import { Row, Select } from 'antd';
import { FormOutlined, SettingOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

import WorkflowActionNode from './WorkflowActionNode';
import WorkflowHandle from './WorkflowHandle';
import WorkflowSimpleMappingConfigureDrawer from './WorkflowSimpleMappingConfigureDrawer';
import BorderlessButton from '../../common/buttons/BorderlessButton';
import {
  updateData,
  locateWorkflowParentElement,
} from './workflowHelpers';
import { AdditionalItem, AdditionalLabel } from './AssignmentDropdownItems';
import UserAssignmentSelector from './selectors/UserAssignmentSelector';
import FormColorPicker from '../../common/inputs/FormColorPicker';
import UpdateAddTimestampCheckbox from './UpdateAddTimestampCheckbox';
import FormWorkflowContext from './FormWorkflowContext';
import OnTraccrCheckbox from '../../common/inputs/OnTraccrCheckbox';
import { getIdMap } from '../../helpers/helpers';
import { resolveFormDivisionIds } from '../formHelpers';
import { CARD_DATA } from './workflow.constants';
import OnTraccrTextInput from '../../common/inputs/OnTraccrTextInput';

const { Option } = Select;

const getCardFields = (cardTemplate) => {
  const {
    fields: sections,
  } = cardTemplate ?? {};
  const cardFields = [];
  cardFields.push({
    id: 'title',
    key: 'title',
    text: 'Title',
    types: ['text', 'dropdown', 'staticText'],
    dataType: undefined,
    subDataType: undefined,
  });
  sections?.forEach((section) => {
    const { fields, name: sectionName } = section;
    fields?.forEach((field) => {
      if (!field?.id || !field?.configProps) return;

      const {
        id,
        selectedType,
        configProps: {
          title, dataType, subDataType, subType,
        } = {},
      } = field;
      const textTitle = `${sectionName} - ${title}`;

      const isCustomDropdown = selectedType === 'dropdown'
        && dataType === 'Custom';
      const row = isCustomDropdown ? {
        id,
        key: id,
        text: textTitle,
        types: [selectedType, 'staticText'],
        allowMultipleStaticText: true,
        subDataType: undefined,
      } : {
        id,
        key: id,
        text: textTitle,
        type: selectedType,
        dataType,
        subDataType: subDataType ?? subType,
      };

      cardFields.push(row);
    });
  });
  return cardFields;
};

const requiredFields = new Set(['title']);
const HOVER_TEXT = 'This step can create a board card based on the fields submitted.';
const DISABLED_TEXT = 'Add at least one Text, Static Text, or Dropdown Field to enable this step';
const AUTHOR_HELP = 'The card will be assigned to the form\'s author';
const ERROR_DETAILS = {
  content: (
    <div style={{ maxWidth: 300 }}>
      This step requires the following fields to be configured: <br />
      - <b>Select Board</b><br />
      - <b>Select Status</b><br />
      - <b>Title Field Mapping</b> (set via <b>Configure</b>)
    </div>
  ),
};

const LINK_TYPE_MAP = {
  Projects: 'projectId',
  Customers: 'customerId',
  Materials: 'materialId',
  Users: 'userId',
  Equipment: 'equipmentId',
};

export default function WorkflowCreateBoardCardNode({
  isDisplay,
  setElements,
  setDataMap,
  divisionId,
  divisionIds = [],
  name,
  sections = [],
  isExternalForm,
  setDisableNext,
} = {}) {
  const relevantDivisionIds = resolveFormDivisionIds({ divisionIds, divisionId });

  return function WorkflowCreateBoardCardNodeComponent({
    id,
    draggable,
    data = {},
    disabled,
  }) {
    const { t } = useTranslation();

    const {
      fieldMappings: initialFieldMappings = {},
      sectionMappings: initialSectionMappings = {},
      board: initialBoard,
      status: initialStatus,
      cardColor: initialCardColor = '#FFFFFFFF',
      users: initialUsers = [],
      cardTypeFields: initialCardTypeFields = [],
      cardTypeId: initialCardTypeId,
      linkField: initialLinkField,
      addTimestamps: initialAddTimestamps,
      timestampConfig: initialTimestampConfig,
      detectDuplicates: initialDetectDuplicates = false,
      checkAllBoardsWithSameCardType: initialCheckAllBoardsWithSameCardType = false,
      allowConfigureConflictDetection: initialAllowConfigureConflictDetection = false,
      fillCardDropdown: initialFillCardDropdown = false,
      fillCardDropdownFieldId: initialFillCardDropdownFieldId,
    } = data;

    const title = `Create Board Card${disabled ? ' - DISABLED' : ''}`;

    const boards = useSelector((state) => state.boards.boards);
    const boardStatuses = useSelector((state) => state.boards.boardStatuses);
    const cardTemplates = useSelector((state) => state.boards.cardTemplates);
    const { paidFlags = [] } = useSelector((state) => state.settings.company);
    const hasAdvancedSearch = paidFlags.includes('ADVANCED_SEARCH');

    const boardStatusMap = useMemo(() => (
      boardStatuses?.reduce((acc, status) => {
        if (!acc[status.boardId]) acc[status.boardId] = [];
        acc[status.boardId].push(status);
        return acc;
      }, {}) ?? {}
    ), [boardStatuses]);

    const [showDrawer, setShowDrawer] = useState(false);
    const [fieldMappings, setFieldMappings] = useState(initialFieldMappings);
    const [sectionMappings, setSectionMappings] = useState(initialSectionMappings);
    const [selectedBoard, setSelectedBoard] = useState(initialBoard);
    const [selectedStatus, setSelectedStatus] = useState(initialStatus);
    const [selectedCardColor, setSelectedCardColor] = useState(initialCardColor);
    const [selectedUsers, setSelectedUsers] = useState(initialUsers);
    const [mappedCardFields, setMappedCardFields] = useState([]);
    const [cardTypeFields, setCardTypeFields] = useState(initialCardTypeFields);
    const [cardTypeId, setCardTypeId] = useState(initialCardTypeId);
    const [linkField, setLinkField] = useState(initialLinkField);
    const [selectedAddTimestamps, setSelectedAddTimestamps] = useState(initialAddTimestamps);
    const [detectDuplicates, setDetectDuplicates] = useState(initialDetectDuplicates);
    const [checkAllBoardsWithSameCardType, setCheckAllBoardsWithSameCardType] = useState(
      initialCheckAllBoardsWithSameCardType,
    );
    const [
      allowConfigureConflictDetection,
      setAllowConfigureConflictDetection,
    ] = useState(initialAllowConfigureConflictDetection);
    const [fillCardDropdown, setFillCardDropdown] = useState(initialFillCardDropdown);
    const [fillCardDropdownFieldId, setFillCardDropdownFieldId] = useState(
      initialFillCardDropdownFieldId,
    );
    const [selectedTimestampConfig, setSelectedTimestampConfig] = useState(
      initialTimestampConfig || {
        source: 'stepExecution',
        includeTime: false,
        includeInitials: false,
      },
    );
    const {
      elements: contextElements = [],
      dataMap: contextDataMap = {},
    } = useContext(FormWorkflowContext) ?? {};

    // Merge data with dataMap so drawer receives latest state (e.g. resetMaterialsAddedDate)
    const nodeData = useMemo(() => ({
      ...data,
      ...(contextDataMap[id] || {}),
    }), [data, contextDataMap, id]);

    const anotherNodeHasDetectDuplicates = useMemo(() => (
      contextElements.some((element) => (
        element.id !== id
        && element.type === 'createBoardCard'
        && contextDataMap[element.id]?.detectDuplicates
      ))
    ), [contextElements, contextDataMap, id]);

    const detectDuplicatesHoverText = useMemo(() => {
      if (anotherNodeHasDetectDuplicates) {
        return 'Another Create Board Card step already has this setting enabled.';
      }
      if (hasAdvancedSearch) {
        return 'Check for existing cards with matching field values before creating.';
      }
      return 'This feature is only available with advanced search.';
    }, [anotherNodeHasDetectDuplicates, hasAdvancedSearch]);

    useEffect(() => {
      if (!setDisableNext) return;

      const disableNext = !selectedBoard || !selectedStatus || !fieldMappings?.title;
      setDisableNext((prev) => ({ ...prev, [id]: disableNext }));
    }, [selectedBoard, selectedStatus, fieldMappings, setDisableNext, id]);

    const openDrawer = useCallback(() => setShowDrawer(true), []);
    const closeDrawer = useCallback(() => setShowDrawer(false), []);

    const onFieldMappingsChange = useCallback((newMappings) => {
      if (!setDataMap || !id) return;
      setFieldMappings(newMappings);
      setDataMap(updateData(id, { fieldMappings: newMappings }));
    }, [setDataMap, id]);

    const onSectionMappingsChange = useCallback((newMappings) => {
      if (!setDataMap || !id) return;
      setSectionMappings(newMappings);
      setDataMap(updateData(id, { sectionMappings: newMappings }));
    }, [setDataMap, id]);

    const onBoardChange = useCallback((newBoard) => {
      if (!setDataMap || !id) return;
      setSelectedBoard(newBoard);
      setFillCardDropdown(false);
      setFillCardDropdownFieldId(undefined);
      setDataMap(updateData(id, {
        board: newBoard,
        status: undefined,
        fillCardDropdown: false,
        fillCardDropdownFieldId: null,
      }));
      setSelectedStatus(undefined);
    }, [setDataMap, id]);

    const onStatusChange = useCallback((newStatus) => {
      if (!setDataMap || !id) return;
      setSelectedStatus(newStatus);
      setDataMap(updateData(id, { status: newStatus }));
    }, [setDataMap, id]);

    const onStatusTextInputChange = useCallback((e) => {
      const newStatus = e.target.value;
      onStatusChange(newStatus);
    }, [onStatusChange]);

    const onColorChange = useCallback((newColor) => {
      if (!setDataMap || !id) return;
      setSelectedCardColor(newColor);
      setDataMap(updateData(id, { cardColor: newColor }));
    }, [setDataMap, id]);

    const onLinkFieldChange = useCallback((newLinkFieldId, newLinkField) => {
      if (!setDataMap || !id) return;
      if (!newLinkField || !(newLinkField.dataType in LINK_TYPE_MAP)) {
        setLinkField();
        setDataMap(updateData(id, { linkField: null, linkFieldType: null }));
      } else {
        setLinkField(newLinkFieldId);
        setDataMap(updateData(id, {
          linkField: newLinkFieldId,
          linkFieldType: LINK_TYPE_MAP[newLinkField.dataType],
        }));
      }
    }, [setDataMap, id]);

    const onUsersChange = useCallback((newSelectedUsers) => {
      setDataMap((dataMap) => ({
        ...dataMap,
        [id]: { ...dataMap[id], users: newSelectedUsers },
      }));
      setSelectedUsers(newSelectedUsers);
    }, [id]);

    const onTimestampChanged = useCallback((newValue) => {
      setSelectedAddTimestamps(newValue);
      setDataMap(updateData(id, { addTimestamps: newValue }));
    }, [setDataMap, id]);

    const onTimestampConfigChanged = useCallback((newConfig) => {
      setSelectedTimestampConfig(newConfig);
      setDataMap(updateData(id, { timestampConfig: newConfig }));
    }, [setDataMap, id]);

    const onDetectDuplicatesChange = useCallback((newValue) => {
      if (!hasAdvancedSearch) return;
      setDetectDuplicates(newValue);
      const changes = { detectDuplicates: newValue };
      if (!newValue) {
        setCheckAllBoardsWithSameCardType(false);
        setAllowConfigureConflictDetection(false);
        changes.checkAllBoardsWithSameCardType = false;
        changes.allowConfigureConflictDetection = false;
      }
      setDataMap(updateData(id, changes));
    }, [setDataMap, id, hasAdvancedSearch]);

    const onCheckAllBoardsWithSameCardTypeChange = useCallback((newValue) => {
      setCheckAllBoardsWithSameCardType(newValue);
      setDataMap(updateData(id, { checkAllBoardsWithSameCardType: newValue }));
    }, [setDataMap, id]);

    const onAllowConfigureConflictDetectionChange = useCallback((newValue) => {
      setAllowConfigureConflictDetection(newValue);
      setDataMap(updateData(id, { allowConfigureConflictDetection: newValue }));
    }, [setDataMap, id]);

    const onFillCardDropdownChange = useCallback((newValue) => {
      setFillCardDropdown(newValue);
      const changes = { fillCardDropdown: newValue };
      if (!newValue) {
        setFillCardDropdownFieldId(undefined);
        changes.fillCardDropdownFieldId = null;
      }
      setDataMap(updateData(id, changes));
    }, [setDataMap, id]);

    const onFillCardDropdownFieldIdChange = useCallback((newFieldId) => {
      setFillCardDropdownFieldId(newFieldId);
      setDataMap(updateData(id, { fillCardDropdownFieldId: newFieldId }));
    }, [setDataMap, id]);

    const boardFieldsWithFilter = useMemo(() => {
      const fields = [];
      sections.forEach(({ name: sectionName, fields: sectionFields = [] }) => {
        sectionFields?.forEach((field) => {
          const { id: fieldId, selectedType, configProps = {} } = field;
          const { title: fieldName, dataType, boardCardTypeFilter } = configProps;
          if (selectedType === 'dropdown' && dataType === 'Boards' && boardCardTypeFilter) {
            fields.push({ id: fieldId, title: `${sectionName} - ${fieldName}`, boardCardTypeFilter });
          }
        });
      });
      return fields;
    }, [sections]);

    const boardFieldMap = useMemo(() => getIdMap(boardFieldsWithFilter), [boardFieldsWithFilter]);

    const mappedBoards = useMemo(() => {
      const options = Object.keys(boards).map((boardId) => (
        <Option key={boardId} value={boardId} label={boards[boardId].title}>
          {boards[boardId].title}
        </Option>
      ));
      boardFieldsWithFilter.forEach(({ id: fieldId, title }) => {
        options.push(
          <Option key={fieldId} value={fieldId} label={title}>
            {title}
          </Option>,
        );
      });
      return options;
    }, [boards, boardFieldsWithFilter]);

    const additionalUserOptions = useMemo(() => {
      const parentCreateUserNode = locateWorkflowParentElement(id, 'createUser', contextElements);

      return parentCreateUserNode ? [{
        id: 'newlyCreatedUser',
        children: <AdditionalItem title="Newly Created User" help="The user created by the connected Create User step" />,
        label: <AdditionalLabel title="Newly Created User" help="The user created by the connected Create User step" />,
      }] : [];
    }, [contextElements, id]);

    useEffect(() => {
      if (selectedBoard) {
        const selectedCardType = boards[selectedBoard]?.cardTypeId
          ?? boardFieldMap[selectedBoard]?.boardCardTypeFilter;
        if (selectedCardType) {
          setCardTypeId(selectedCardType);
          setMappedCardFields(getCardFields(cardTemplates[selectedCardType]));
          setCardTypeFields(cardTemplates[selectedCardType]?.fields);
        }
      }
    }, [selectedBoard, boardFieldMap]);

    useEffect(() => {
      if (!setDataMap || !id) return;
      setDataMap(updateData(id, { cardTypeId }));
    }, [cardTypeId]);

    useEffect(() => {
      if (!setDataMap || !id) return;
      setDataMap(updateData(id, { cardTypeFields }));
    }, [cardTypeFields]);

    useEffect(() => {
      if (!setDataMap || !id) return;
      setDataMap(updateData(id, { linkField }));
    }, [linkField]);

    const mappedStatuses = useMemo(() => {
      const {
        [selectedBoard]: statuses,
      } = boardStatusMap;
      if (!statuses) return [];
      const options = statuses.map?.((status) => (
        <Option key={status.id} value={status.id} label={status.title}>
          {status.title}
        </Option>
      )) ?? [];
      return options;
    }, [boardStatusMap, selectedBoard]);

    useEffect(() => {
      const {
        [selectedBoard]: statuses,
      } = boardStatusMap;
      if (statuses?.length > 0 && !statuses?.some?.((status) => status.id === selectedStatus)) {
        onStatusChange(undefined);
      }
    }, [boardStatusMap, selectedBoard, selectedStatus]);

    const sourceSections = useMemo(() => (
      [CARD_DATA, {
        name: 'Form Data',
        fields: [{
          id: 'formNumber',
          configProps: {
            title: 'Form Number',
          },
          selectedType: 'text',
        }],
      }].concat(sections)
    ), [sections]);

    const linkSections = useMemo(() => {
      const ls = [];
      sections.forEach(({ name: sectionName, fields = [] }) => {
        fields?.forEach((field) => {
          const {
            configProps: {
              title: fieldName,
              dataType,
            } = {},
            selectedType,
            id: fieldId,
          } = field;
          if (selectedType !== 'dropdown' || !(dataType in LINK_TYPE_MAP)) return;
          ls.push({
            value: fieldId,
            label: `${sectionName} - ${fieldName}`,
            dataType,
          });
        });
      });
      return ls;
    }, [sections]);

    const cardDropdownSections = useMemo(() => {
      const fields = [];
      sections.forEach(({ name: sectionName, fields: sectionFields = [] }) => {
        sectionFields?.forEach((field) => {
          const {
            id: fieldId,
            selectedType,
            configProps: { title: fieldName, dataType, subDataType } = {},
          } = field;
          if (
            selectedType === 'dropdown'
            && dataType === 'Cards'
            && subDataType === selectedBoard
          ) {
            fields.push({ value: fieldId, label: `${sectionName} - ${fieldName}` });
          }
        });
      });
      return fields;
    }, [sections, selectedBoard]);

    return (
      <WorkflowActionNode
        id={id}
        title={title}
        type="createBoardCard"
        Icon={FormOutlined}
        draggable={draggable}
        isDisplay={isDisplay || disabled}
        onNodeUpdate={(...args) => {
          setElements(...args);
          if (setDisableNext) {
            setDisableNext((prev) => ({ ...prev, [id]: false }));
          }
        }}
        hover={disabled ? DISABLED_TEXT : HOVER_TEXT}
        style={disabled ? { opacity: 0.7 } : {}}
        errorPopover={
          !selectedBoard || !selectedStatus || !fieldMappings?.title
            ? ERROR_DETAILS
            : null
            }
      >
        {!draggable
          && (
          <div>
            <Row style={{ marginTop: 10 }}>
              Select
              {' '}
              {t('Board')}
              {' '}
            </Row>
            <Row style={{ margin: '10px 0px' }}>
              <Select
                onChange={onBoardChange}
                value={selectedBoard}
                showSearch
                optionFilterProp="label"
              >
                {mappedBoards}
              </Select>
            </Row>
            <Row style={{ marginTop: 10 }}>
              Select
              {' '}
              {t('Status')}
              {' '}
            </Row>
            <Row style={{ margin: '10px 0px' }}>
              {
                selectedBoard in boardFieldMap
                  ? (
                    <OnTraccrTextInput
                      value={selectedStatus}
                      onChange={onStatusTextInputChange}
                    />
                  )
                  : (
                    <Select
                      onChange={onStatusChange}
                      value={selectedStatus}
                      showSearch
                      disabled={!selectedBoard}
                    >
                      {mappedStatuses}
                    </Select>
                  )
              }
            </Row>
            <Row style={{ marginTop: 10 }}>
              Select
              {' '}
              {t('Card Color')}
              {' '}
            </Row>
            <FormColorPicker isNotDisplay value={selectedCardColor} onChange={onColorChange} />
            <UserAssignmentSelector
              text="Assign user(s):"
              type="user"
              isDisplay={isDisplay}
              draggable={draggable}
              divisions={relevantDivisionIds}
              onChange={onUsersChange}
              selected={selectedUsers}
              showFields
              sections={sections}
              showFormAuthor={!isExternalForm}
              formAuthorHelperText={AUTHOR_HELP}
              additionalOptions={additionalUserOptions}
            />
            <Row style={{ marginTop: 10 }}>
              Select Link Field
            </Row>
            <Row style={{ margin: '10px 0px' }}>
              <Select
                onChange={onLinkFieldChange}
                value={linkField}
                showSearch
                options={linkSections}
                dropdownMatchSelectWidth={false}
                allowClear
              />
            </Row>
            {cardDropdownSections.length > 0 && (
              <Row style={{ marginTop: 10 }}>
                <OnTraccrCheckbox
                  label="Fill Card Dropdown"
                  value={fillCardDropdown}
                  onChange={onFillCardDropdownChange}
                  hoverNode="When this card is created, automatically fill a card dropdown field in this form with the newly created card."
                />
              </Row>
            )}
            {fillCardDropdown && cardDropdownSections.length > 0 && (
              <>
                <Row style={{ marginTop: 5 }}>
                  Select Card Dropdown Field
                </Row>
                <Row style={{ margin: '10px 0px' }}>
                  <Select
                    onChange={onFillCardDropdownFieldIdChange}
                    value={fillCardDropdownFieldId}
                    options={cardDropdownSections}
                    dropdownMatchSelectWidth={false}
                    allowClear
                  />
                </Row>
              </>
            )}
            <Row style={{ marginTop: 10 }}>
              <OnTraccrCheckbox
                label="Detect Duplicate Cards"
                value={detectDuplicates}
                onChange={onDetectDuplicatesChange}
                disabled={!hasAdvancedSearch || anotherNodeHasDetectDuplicates}
                hoverNode={detectDuplicatesHoverText}
              />
            </Row>
            {detectDuplicates && (
              <>
                <OnTraccrCheckbox
                  style={{ marginTop: 5 }}
                  label="Check all Boards with same Card Type"
                  value={checkAllBoardsWithSameCardType}
                  onChange={onCheckAllBoardsWithSameCardTypeChange}
                />
                <OnTraccrCheckbox
                  style={{ marginTop: 5 }}
                  label="Allow Conflict Detection Configuration"
                  value={allowConfigureConflictDetection}
                  onChange={onAllowConfigureConflictDetectionChange}
                />
              </>
            )}
            <Row style={{ margin: '20px 0px' }}>
              <BorderlessButton
                title="Configure"
                style={{ margin: 5 }}
                iconNode={<SettingOutlined />}
                onClick={openDrawer}
              />
            </Row>
            <UpdateAddTimestampCheckbox
              onChange={onTimestampChanged}
              initialChecked={initialAddTimestamps}
              value={selectedAddTimestamps}
              sections={sections}
              onTimestampConfigChange={onTimestampConfigChanged}
              timestampConfig={selectedTimestampConfig}
              showInitialsCheckbox
            />
            <WorkflowSimpleMappingConfigureDrawer
              id={id}
              visible={showDrawer}
              onClose={closeDrawer}
              onSubmit={closeDrawer}
              sourceName={name}
              sourceSections={sourceSections}
              fieldMappings={fieldMappings}
              onFieldMappingsChange={onFieldMappingsChange}
              sectionMappings={sectionMappings}
              onSectionMappingsChange={onSectionMappingsChange}
              targetSections={cardTypeFields}
              shouldAllowDuplicateSectionMapping
              data={nodeData}
              setDataMap={setDataMap}
              fields={mappedCardFields}
              requiredFields={requiredFields}
              detectDuplicates={detectDuplicates}
              mapDropdown
              canResetMaterialsAddedDate
            />
            <WorkflowHandle type="target" position="top" disabled={isDisplay} />
            { !!paidFlags?.includes('COBRA_CUSTOM_PMA') && (
              <WorkflowHandle type="source" position="bottom" disabled={isDisplay} />
            )}
          </div>
          )}
      </WorkflowActionNode>
    );
  };
}

WorkflowCreateBoardCardNode.propTypes = {
  isDisplay: PropTypes.bool,
  setElements: PropTypes.func,
  setDataMap: PropTypes.func,
  divisionId: PropTypes.string,
  name: PropTypes.string,
  sections: PropTypes.arrayOf(PropTypes.string),
  isExternalForm: PropTypes.bool,
  setDisableNext: PropTypes.func,
};
